import React from "react";
import { useInView } from "react-intersection-observer";
import {
  HiOutlineLightningBolt,
  HiOutlineUsers,
  HiOutlineShieldCheck,
} from "react-icons/hi";

/* ─── image URLs ─────────────────────────────────────────────────────────── */
const heroImg =
  "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=900&q=80";
// white Tesla-style EV on road

const missionImg =
  "https://images.unsplash.com/photo-1620714223084-8fcacc2dbed5?auto=format&fit=crop&w=900&q=80";
// EV charging station

/* ─── testimonial data ───────────────────────────────────────────────────── */
const testimonials = [
  {
    quote:
      "Switching to an electric car cut my monthly running costs in half and the service support has been excellent.",
    name: "Tobi A.",
    role: "EV owner, Abuja",
  },
  {
    quote:
      "The team walked me through financing and charging options before I bought anything. No pressure, just honest advice.",
    name: "Ngozi E.",
    role: "EV owner, Lagos",
  },
  {
    quote:
      "Fast delivery, transparent pricing, and a vehicle that's been completely reliable since day one.",
    name: "Bashir M.",
    role: "Fleet customer",
  },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
const Aboutusp = () => {
  const { ref: headingRef, inView: headingInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { ref: cardsRef, inView: cardsInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div className="bg-white w-full">

      {/* ── Hero band (replaces bare pt-32) ─────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#102a05]  to-[#3d7a0a] text-white pt-36 pb-20 px-6 md:px-10 lg:px-16">
        <span className="text-white/70 font-semibold uppercase tracking-widest text-xs">
          About Us
        </span>
        <h1 className="text-4xl md:text-5xl font-bold mt-3 max-w-2xl leading-tight">
          Driving Nigeria toward cleaner mobility
        </h1>
        <p className="text-white/80 mt-4 max-w-xl leading-relaxed">
          We source, sell, and support electric vehicles built for Nigerian
          roads — making the switch from fuel to electric simple, affordable,
          and reliable.
        </p>
      </div>

      {/* ── Intro section ────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-center px-6 md:px-10 lg:px-16 py-20 gap-12">
        <div className="w-full md:w-[560px]">
          <h2 className="text-3xl md:text-4xl font-bold mb-5 text-gray-900">
            A team built around your journey
          </h2>
          <p className="mb-5 text-gray-600 leading-relaxed">
            Electromob brings electric vehicles to Nigerian roads, built to
            handle local conditions and backed by a team that understands the
            realities of driving here. We source, sell, and support EVs so that
            switching from fuel to electric is simple, affordable, and reliable.
          </p>
          <p className="text-gray-600 leading-relaxed">
            From your first test drive to ongoing maintenance and charging
            support, we're focused on making electric mobility a practical
            choice for individuals and businesses across the country.
          </p>
        </div>

        <div className="flex justify-end w-full md:w-auto">
          <img
            src={heroImg}
            alt="Electromob electric vehicle on the road"
            className="w-full md:w-[500px] h-[380px] md:h-[460px] object-cover rounded-2xl shadow-lg"
          />
        </div>
      </div>

      {/* ── Pillars ──────────────────────────────────────────────────────── */}
      <div className="bg-[#f4faec] py-20 px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row gap-6 justify-between">
          <Pillar
            icon={<HiOutlineLightningBolt className="text-2xl" />}
            title="Our Services"
            text="We supply, sell, and finance electric vehicles, and support every customer with charging guidance, maintenance, and after-sales care."
          />
          <Pillar
            icon={<HiOutlineUsers className="text-2xl" />}
            title="Experience"
            text="Our team has hands-on knowledge of EV technology and Nigerian road conditions, so the advice you get is practical, not generic."
          />
          <Pillar
            icon={<HiOutlineShieldCheck className="text-2xl" />}
            title="Accountability"
            text="Transparent pricing, honest timelines, and dependable support after the sale — we stand behind every vehicle we deliver."
          />
        </div>
      </div>

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      <div className="bg-[#102a05] w-full py-20 px-6 md:px-10 lg:px-16 text-white">
        <p className="mb-3 font-semibold text-xs uppercase tracking-widest text-center text-[#8fe33a]">
          Testimonials
        </p>

        <h2
          ref={headingRef}
          className={`text-3xl font-bold mb-12 text-center transition-all duration-700
            ${headingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          What our customers are saying
        </h2>

        <div
          ref={cardsRef}
          className={`grid gap-6 md:grid-cols-3 transition-all duration-700 delay-200
            ${cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          {testimonials.map((t) => (
            <div
              key={t.name}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4
                         hover:bg-white/10 transition-colors duration-200"
            >
              <p className="text-white/80 text-sm leading-relaxed">
                "{t.quote}"
              </p>
              <div className="mt-auto">
                <p className="font-semibold text-sm">{t.name}</p>
                <p className="text-white/50 text-xs mt-0.5">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mission ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row-reverse justify-between items-center px-6 md:px-10 lg:px-16 py-20 gap-12">
        {/* image first on desktop (row-reverse), below text on mobile */}
        <div className="flex justify-end w-full md:w-auto">
          <img
            src={missionImg}
            alt="Electric vehicle charging station"
            className="w-full md:w-[500px] h-[380px] md:h-[460px] object-cover rounded-2xl shadow-lg"
          />
        </div>

        <div className="w-full md:w-[560px]">
          <span className="text-[#4a8f0f] font-semibold uppercase tracking-widest text-xs">
            Our Mission
          </span>
          <h2 className="text-3xl md:text-4xl font-bold my-4 text-gray-900">
            Building a sustainable future, one trip at a time
          </h2>
          <p className="mb-5 text-gray-600 leading-relaxed">
            We believe Nigeria's transition to electric mobility should be driven
            by access, not exclusivity. That means competitive pricing, vehicles
            suited to local terrain, and a growing network of charging and
            service support.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Every vehicle we put on the road is a step toward lower emissions,
            lower running costs for drivers, and a cleaner future for the
            communities we serve.
          </p>
        </div>
      </div>

    </div>
  );
};

/* ─── sub-components ─────────────────────────────────────────────────────── */
const Pillar = ({ icon, title, text }) => (
  <div className="flex-1 bg-white py-10 px-8 rounded-2xl border border-gray-100 shadow-sm
                  hover:shadow-md transition-shadow duration-200">
    <div className="bg-[#6ab615]/10 text-[#4a8f0f] w-12 h-12 rounded-xl
                    flex items-center justify-center mb-6">
      {icon}
    </div>
    <h3 className="font-bold mb-3 text-gray-900">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{text}</p>
  </div>
);

export default Aboutusp;